import React from 'react';
import { useJobStream } from '../../hooks/useJobStream';
import { useLanguage } from '../../hooks/useLanguage';
import jsonAnimation from "../../assets/thinking-animation.gif";
import { useJobStreamUi } from './JobStreamUiContext';

function useStreamVisualState({
  thinking,
  text,
  isThinking,
  sectionKey,
}) {
  const [fadeKey, setFadeKey] = React.useState(0);
  const thinkingContentRef = React.useRef(null);
  const textContentRef = React.useRef(null);

  React.useEffect(() => {
    if (sectionKey > 0) {
      setFadeKey((prev) => prev + 1);
    }
  }, [sectionKey]);

  React.useEffect(() => {
    if (thinkingContentRef.current && thinking) {
      thinkingContentRef.current.scrollTop = thinkingContentRef.current.scrollHeight;
    }
  }, [thinking]);

  React.useEffect(() => {
    if (textContentRef.current && text) {
      textContentRef.current.scrollTop = textContentRef.current.scrollHeight;
    }
  }, [text]);

  return { fadeKey, thinkingContentRef, textContentRef };
}

function formatMarkdownText(content) {
  if (!content) return content;

  const processBold = (text) => {
    const parts = [];
    let lastIndex = 0;
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      parts.push(<strong key={match.index} className="!font-semibold">{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return content.split('\n').map((line, index) => {
    const headerMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const textLine = headerMatch[2];
      const className = level === 1
        ? '!text-base !font-bold !mt-4 !mb-2 !text-gray-900'
        : level === 2
        ? '!text-sm !font-semibold !mt-3 !mb-2 !text-gray-800'
        : '!text-xs !font-semibold !mt-2 !mb-1 !text-gray-700';

      return (
        <div key={index} className={className}>
          {processBold(textLine)}
        </div>
      );
    }
    return (
      <div key={index} className="text-xs">
        {processBold(line || '\u00A0')}
      </div>
    );
  });
}

function JobStreamVisual({
  isSpanish,
  thinking,
  text,
  isThinking,
  sectionKey,
}) {
  const { fadeKey, thinkingContentRef, textContentRef } = useStreamVisualState({
    thinking,
    text,
    isThinking,
    sectionKey,
  });

  const hasContent = isThinking || (text && text.trim().length > 0);
  if (!hasContent) return null;

  return (
    <div className="mt-4 space-y-3 h-[280px] overflow-y-hidden" style={{ minHeight: '280px' }}>
      {isThinking && (
        <div className="thinking-section shine">
          <div className="flex items-center gap-2 mb-3 p-1">
            <img
              src={jsonAnimation}
              alt="Thinking"
              className="w-4 h-4"
              style={{ display: 'block' }}
            />
            <div className="thinking-section-header text-xs font-semibold !text-default !font-normal">
              <span className="inline-block animate-pulse">{isSpanish ? 'Pensando...' : 'Thinking...'}</span>
            </div>
          </div>
          {thinking && thinking.trim().length > 0 ? (
            <div
              ref={thinkingContentRef}
              key={fadeKey}
              className="text-xs leading-relaxed fade-in-section max-h-[300px] overflow-y-auto scrollbar-hide"
              style={{ minHeight: '20px' }}
            >
              {formatMarkdownText(thinking)}
            </div>
          ) : (
            <div className="text-xs text-gray-500 italic">
              <span className="inline-block animate-pulse">{isSpanish ? 'Analizando documentos...' : 'Analyzing documents...'}</span>
            </div>
          )}
        </div>
      )}

      {text && text.trim().length > 0 && (
        <div className="">
          <div className="flex items-center gap-2 mb-2">
            <img
              src={jsonAnimation}
              alt="Processing"
              className="w-4 h-4"
              style={{ display: 'block' }}
            />
            <div className="thinking-section-header text-xs font-semibold !text-default !font-normal">
              {isSpanish ? 'Procesando...' : 'Processing...'}
            </div>
          </div>
          <div className="relative">
            <div
              ref={textContentRef}
              className="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed prose prose-sm max-w-none font-mono text-xs max-h-[300px] overflow-y-auto scrollbar-hide"
              style={{ minHeight: '20px', wordBreak: 'break-word' }}
            >
              {text}
            </div>
            <div className="absolute top-0 left-0 right-0 h-10 bg-gradient-to-b from-white to-transparent pointer-events-none" style={{ zIndex: 1 }}></div>
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" style={{ zIndex: 1 }}></div>
          </div>
        </div>
      )}
    </div>
  );
}

function JobStreamDisplayViewOnly({ jobId }) {
  const [isSpanish] = useLanguage();
  const ctx = useJobStreamUi();
  const snap = ctx?.uiByJobId?.[String(jobId)] || {};

  return (
    <JobStreamVisual
      isSpanish={isSpanish}
      thinking={snap.thinking || ''}
      text={snap.text || ''}
      isThinking={!!snap.isThinking}
      sectionKey={snap.sectionKey ?? 0}
    />
  );
}

function JobStreamDisplayConnected({
  jobId,
  onStatusUpdate,
  hideUi,
  publishUi,
  publishJobStreamUi,
}) {
  const [isSpanish] = useLanguage();
  const { status, thinking, text, isThinking, sectionKey } = useJobStream(jobId);

  React.useEffect(() => {
    if (!publishUi || !publishJobStreamUi) return;
    publishJobStreamUi(jobId, { thinking, text, isThinking, sectionKey });
  }, [publishUi, publishJobStreamUi, jobId, thinking, text, isThinking, sectionKey]);

  const lastStatusRef = React.useRef(null);
  const updateInProgressRef = React.useRef(false);
  const prevIsThinkingRef = React.useRef(isThinking);
  const onStatusUpdateRef = React.useRef(onStatusUpdate);
  const onStatusUpdateRefForStatus = React.useRef(onStatusUpdate);

  React.useEffect(() => {
    onStatusUpdateRef.current = onStatusUpdate;
    onStatusUpdateRefForStatus.current = onStatusUpdate;
  }, [onStatusUpdate]);

  React.useEffect(() => {
    if (prevIsThinkingRef.current !== isThinking && onStatusUpdateRef.current) {
      prevIsThinkingRef.current = isThinking;
      onStatusUpdateRef.current({ isThinking });
    }
  }, [isThinking]);

  React.useEffect(() => {
    if (!status || !onStatusUpdateRefForStatus.current) return;

    if (status.stage === 'stream_closed') {
      const updateData = {
        areStepsCompleted: true,
        currentStep: 2,
        completedSteps: [0, 1, 2],
        processingStatus: 'completed',
        jobId: status.id || jobId,
        ocrInputId: status.ocrInputId,
      };
      onStatusUpdateRefForStatus.current(updateData);
      lastStatusRef.current = `stream_closed-${status.id || jobId}`;
      updateInProgressRef.current = false;
      return;
    }

    if (updateInProgressRef.current) {
      return;
    }

    const statusKey = JSON.stringify({
      id: status.id || jobId,
      status: status.status,
      stage: status.stage,
      ocrInputId: status.ocrInputId,
      hasResult: !!status.result,
    });

    if (lastStatusRef.current === statusKey) {
      return;
    }
    lastStatusRef.current = statusKey;
    updateInProgressRef.current = true;

    if (status.status === 'completed' && status.result) {
      onStatusUpdateRefForStatus.current({
        areStepsCompleted: true,
        currentStep: 2,
        completedSteps: [0, 1, 2],
        processingStatus: 'completed',
        jobId: status.id || jobId,
        ocrInputId: status.ocrInputId,
      });
      return;
    }

    if (status.status === 'failed' || status.status === 'error') {
      if (process.env.NODE_ENV === 'development') {
        console.error(`❌ [JobStreamDisplay] Stream error for job ${jobId}:`, {
          status: status.status,
          message: status.message,
          error: status.error,
          detail: status.detail,
          code: status.code,
          fullStatus: status,
        });
      }

      onStatusUpdateRefForStatus.current({
        processingStatus: 'error',
        error: status.message || status.error || status.detail || 'Document processing failed',
        jobId: status.id || jobId,
      });
    } else {
      onStatusUpdateRefForStatus.current({
        processingStatus: status.status || 'processing',
        jobId: status.id || jobId,
        isThinking: isThinking,
      });
    }

    setTimeout(() => {
      updateInProgressRef.current = false;
    }, 0);
  }, [status, jobId, isThinking]);

  if (!jobId) {
    return null;
  }

  if (hideUi) {
    return null;
  }

  return (
    <JobStreamVisual
      isSpanish={isSpanish}
      thinking={thinking}
      text={text}
      isThinking={isThinking}
      sectionKey={sectionKey}
    />
  );
}

/**
 * @param {boolean} [hideUi] — run SSE + callbacks only (hidden publisher)
 * @param {boolean} [publishUi] — push thinking/text into JobStreamUiProvider for viewOnly peers
 * @param {boolean} [viewOnly] — render from context only (no second SSE)
 */
export const JobStreamDisplay = ({
  jobId,
  onStatusUpdate,
  hideUi,
  publishUi,
  viewOnly,
}) => {
  const ctx = useJobStreamUi();

  if (viewOnly) {
    if (!jobId) return null;
    return <JobStreamDisplayViewOnly jobId={jobId} />;
  }

  return (
    <JobStreamDisplayConnected
      jobId={jobId}
      onStatusUpdate={onStatusUpdate}
      hideUi={hideUi}
      publishUi={publishUi}
      publishJobStreamUi={ctx?.publishJobStreamUi}
    />
  );
};
